/**
 * `SCR-LEADS` and `SCR-LEAD` — the site's commissions, in the back office.
 * WIRE-1, 7 September 2026. OWNER: web-frontend-engineer.
 *
 * Until this screen a commission placed on the website reached a file on the
 * demo server and an inbox, and the CRM never heard of it. Now it is a LEAD:
 * the queue is what is waiting; one lead is who he is, whether the house has
 * his measure, and what he chose, as the site sent it; and the two things a
 * tailor with four minutes can do with it — put him on file (a client record
 * from the lead, or attach it to a man already on file, with what he chose as
 * the first note on his record), or close it with a reason. Nothing is
 * deleted, and no status board is maintained: `new` IS the queue.
 *
 * WHAT THIS SCREEN NEVER SHOWS: a measurement. The site does not send one and
 * the CRM refuses one; "his measure" here is the fact he stated, and the
 * sentence beside it says to check the record before anything is cut.
 *
 * AMENDED 18 October 2026, LAW 58 ("at check out i need it to give the actual
 * product skews and color codes etc for swatches of fabric and books ... then
 * we need to create a backend where paul has it"): one lead also shows the
 * mill's References, piece by piece, as the server read them out of what the
 * site sent: the cloth's book, code, colour, weight, fibre and weave; the
 * lining; each shirt with how many. The lead list has no search, so there was
 * none to widen to the code and the book.
 */

import { useState } from 'react';
import type { LeadDetail, LeadReference, LeadRow, LeadStatus } from '@pt/core';
import { t, type CopyKey } from '@pt/copy';
import { dateInWords } from '@pt/view';
import { api } from '../../lib/api.ts';
import { useResource, useSubmission } from '../../lib/resource.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { BackOfficeTable, RefusalStatement, SearchField, type Column } from '../../components/backoffice.tsx';
import { Button, FactLine, FactPair, Statement, TextInput } from '../../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement, Refused } from '../../components/states.tsx';

type Filter = LeadStatus | 'all';
const FILTERS: readonly Filter[] = ['new', 'client', 'closed', 'all'];

export function statusLabel(status: LeadStatus): string {
  switch (status) {
    case 'new':
      return t('leads.status.new');
    case 'client':
      return t('leads.status.client');
    case 'closed':
      return t('leads.status.closed');
  }
}

function filterLabel(filter: Filter): string {
  switch (filter) {
    case 'new':
      return t('leads.filter.new');
    case 'client':
      return t('leads.filter.client');
    case 'closed':
      return t('leads.filter.closed');
    case 'all':
      return t('leads.filter.all');
  }
}

export function measureWord(unmeasured: boolean): string {
  return unmeasured ? t('leads.measure.notTaken') : t('leads.measure.taken');
}

export function LeadsScreen() {
  const { navigate } = useNavigation();
  const [filter, setFilter] = useState<Filter>('new');
  const leads = useResource(() => api.leads({ status: filter }), [filter]);
  const rows = leads.data?.leads ?? [];

  const columns: readonly Column<LeadRow>[] = [
    { id: 'received', headingKey: 'leads.column.received', render: (row) => dateInWords(row.receivedAt) },
    { id: 'name', headingKey: 'leads.column.name', render: (row) => row.contactName },
    { id: 'garment', headingKey: 'leads.column.garment', render: (row) => row.garment ?? '' },
    {
      id: 'cloth',
      headingKey: 'leads.column.cloth',
      render: (row) => [row.clothCode, row.clothName].filter((v) => v !== null).join(' · '),
    },
    { id: 'measure', headingKey: 'leads.column.measure', render: (row) => measureWord(row.unmeasured) },
    { id: 'status', headingKey: 'leads.column.status', render: (row) => statusLabel(row.status) },
  ];

  return (
    <AppShell title={t('leads.heading')}>
      <Statement>{t('leads.lede')}</Statement>
      <div className="pt-actions" role="group" aria-label={t('leads.filter.label')}>
        {FILTERS.map((option) => (
          <Button
            key={option}
            variant="secondary"
            aria-pressed={filter === option}
            onClick={() => setFilter(option)}
          >
            {filterLabel(option)}
          </Button>
        ))}
      </div>

      {leads.phase === 'skeleton' || leads.phase === 'idle' ? (
        <LoadingRegion rows={6} variant="table" slow={leads.slow} label={t('leads.heading')} />
      ) : null}
      {leads.phase === 'error' ? <FetchError onRetry={leads.reload} /> : null}
      {leads.phase !== 'ready' ? null : rows.length === 0 ? (
        <Statement>{t('leads.empty')}</Statement>
      ) : (
        <BackOfficeTable
          caption={t('leads.heading')}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.leadId}
          rowHref={(row) => `/atelier/leads/${row.leadId}`}
          onOpenRow={(row) => navigate(`/atelier/leads/${row.leadId}`)}
        />
      )}
    </AppShell>
  );
}

/** Who he is, whether the house has his measure, and what he chose — as the site sent it. */
export function LeadSummary({ lead }: { lead: LeadDetail }) {
  return (
    <>
      <section className="pt-block">
        <h2 className="pt-cutsheet__blockTitle">{t('lead.contact.heading')}</h2>
        <FactPair label={t('leads.column.name')}>{lead.contactName}</FactPair>
        <FactPair label={t('lead.contact.email')}>{lead.contactEmail ?? t('lead.contact.none')}</FactPair>
        <FactPair label={t('lead.contact.phone')}>{lead.contactPhone ?? t('lead.contact.none')}</FactPair>
        <FactPair label={t('lead.reference')}>
          <span className="pt-identifier">{lead.siteReference}</span>
        </FactPair>
        <FactPair label={t('lead.received')}>{dateInWords(lead.receivedAt)}</FactPair>
      </section>
      <section className="pt-block">
        <h2 className="pt-cutsheet__blockTitle">{t('lead.measure.heading')}</h2>
        <Statement>{lead.unmeasured ? t('lead.measure.notTaken') : t('lead.measure.taken')}</Statement>
      </section>
      <section className="pt-block">
        <h2 className="pt-cutsheet__blockTitle">{t('lead.spec.heading')}</h2>
        {lead.summary.length === 0 ? (
          <Statement>{t('lead.spec.empty')}</Statement>
        ) : (
          lead.summary.map((line) => (
            <FactPair key={line.label} label={line.label}>
              {line.value}
            </FactPair>
          ))
        )}
        <details className="pt-block">
          <summary>{t('lead.spec.raw')}</summary>
          <pre className="pt-identifier">{JSON.stringify(lead.specification, null, 2)}</pre>
        </details>
      </section>
      <LeadReferences references={lead.references} />
    </>
  );
}

const PART_KEY = {
  cloth: 'lead.refs.cloth',
  lining: 'lead.refs.lining',
  shirt: 'lead.refs.shirt',
} as const satisfies Record<LeadReference['part'], CopyKey>;

/**
 * AMENDED 18 October 2026, LAW 58 · the mill's references, one piece at a time,
 * so Paul can order the cloth and send the swatch without opening the record as
 * sent. A fact the site did not send is not a row, and a lead with no
 * references has no block. The square is drawn from the colour code the server
 * passed (it passes only a code that IS one), it is decorative because the code
 * is printed beside it, and the sentence above says the book is the truth.
 */
export function LeadReferences({ references }: { references: readonly LeadReference[] }) {
  if (references.length === 0) return null;
  return (
    <section className="pt-block">
      <h2 className="pt-cutsheet__blockTitle">{t('lead.refs.heading')}</h2>
      {references.some((piece) => piece.hex !== null) ? <Statement>{t('lead.refs.colour.note')}</Statement> : null}
      {references.map((piece, index) => (
        <dl className="pt-block" key={`${piece.part}-${index}`}>
          <FactPair label={t(PART_KEY[piece.part])}>{piece.name ?? piece.code ?? piece.book ?? ''}</FactPair>
          {piece.book === null ? null : <FactPair label={t('lead.refs.book')}>{piece.book}</FactPair>}
          {piece.code === null ? null : (
            <FactPair label={t('lead.refs.code')}>
              <span className="pt-identifier">{piece.code}</span>
            </FactPair>
          )}
          {piece.shade === null && piece.hex === null ? null : (
            <FactPair label={t('lead.refs.colour')}>
              {piece.hex === null ? null : (
                <span className="pt-colourmark" style={{ backgroundColor: piece.hex }} aria-hidden="true" />
              )}
              {piece.shade}
              {piece.shade !== null && piece.hex !== null ? ' · ' : null}
              {piece.hex === null ? null : <span className="pt-identifier">{piece.hex}</span>}
            </FactPair>
          )}
          {piece.weight === null ? null : <FactPair label={t('lead.refs.weight')}>{piece.weight}</FactPair>}
          {piece.fibre === null ? null : <FactPair label={t('lead.refs.fibre')}>{piece.fibre}</FactPair>}
          {piece.weave === null ? null : <FactPair label={t('lead.refs.weave')}>{piece.weave}</FactPair>}
          {piece.qty === null ? null : <FactPair label={t('lead.refs.qty')}>{String(piece.qty)}</FactPair>}
        </dl>
      ))}
    </section>
  );
}

export function LeadScreen({ leadId }: { leadId: string }) {
  const { navigate } = useNavigation();
  const detail = useResource(() => api.lead(leadId), [leadId]);
  const toClient = useSubmission();
  const closing = useSubmission();
  const [mode, setMode] = useState<'idle' | 'attach' | 'close'>('idle');
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState('');
  const clients = useResource(
    () => (mode === 'attach' ? api.atelierClients({}) : Promise.resolve({ clients: [] })),
    [mode],
  );

  const lead = detail.data?.lead ?? null;

  const putOnFile = (clientId: string | null) =>
    void toClient.run(async () => {
      const made = await api.leadToClient(leadId, clientId === null ? {} : { clientId });
      detail.reload();
      setMode('idle');
      return made;
    });

  const close = () =>
    void closing.run(async () => {
      const closed = await api.closeLead(leadId, { reason: reason.trim() });
      detail.reload();
      setMode('idle');
      return closed;
    });

  if (detail.rejection !== null && detail.rejection.status === 404) {
    return (
      <AppShell title={t('lead.heading')}>
        <Refused kind={404} />
      </AppShell>
    );
  }

  const matches = (clients.data?.clients ?? []).filter(
    (row) =>
      search.trim() !== '' &&
      (row.displayName.toLowerCase().includes(search.toLowerCase()) ||
        row.clientRef.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <AppShell title={t('lead.heading')}>
      {detail.phase === 'skeleton' || detail.phase === 'idle' ? (
        <LoadingRegion rows={8} variant="block" slow={detail.slow} label={t('lead.heading')} />
      ) : null}
      {detail.phase === 'error' ? <FetchError onRetry={detail.reload} /> : null}

      {lead === null ? null : (
        <>
          <LeadSummary lead={lead} />

          {lead.status === 'client' ? (
            <section className="pt-block">
              <Statement>{t('lead.done.client')}</Statement>
              {lead.clientRef === null ? null : (
                <FactPair label={t('lead.done.ref')}>
                  <span className="pt-identifier">{lead.clientRef}</span>
                </FactPair>
              )}
              {lead.handledByName === null || lead.handledAt === null ? null : (
                <FactLine>{t('lead.handled', { authorName: lead.handledByName, at: dateInWords(lead.handledAt) })}</FactLine>
              )}
              <div className="pt-actions">
                <Button onClick={() => navigate(`/atelier/clients/${lead.clientId ?? ''}`)}>{t('lead.done.open')}</Button>
              </div>
            </section>
          ) : null}

          {lead.status === 'closed' ? (
            <section className="pt-block">
              <Statement>{t('lead.done.closed')}</Statement>
              <FactPair label={t('lead.closed.reason')}>{lead.closedReason ?? ''}</FactPair>
              {lead.handledByName === null || lead.handledAt === null ? null : (
                <FactLine>{t('lead.handled', { authorName: lead.handledByName, at: dateInWords(lead.handledAt) })}</FactLine>
              )}
            </section>
          ) : null}

          {lead.status === 'new' ? (
            <section className="pt-block">
              {toClient.rejection === null ? null : <RefusalStatement rejection={toClient.rejection} />}
              {closing.rejection === null ? null : <RefusalStatement rejection={closing.rejection} />}
              {toClient.offline || closing.offline ? <OfflineStatement /> : null}

              {mode === 'idle' ? (
                <>
                  <Statement>{t('lead.action.client.help')}</Statement>
                  <div className="pt-actions">
                    <Button onClick={() => putOnFile(null)} busy={toClient.busy}>
                      {t('lead.action.client')}
                    </Button>
                    <Button variant="secondary" onClick={() => setMode('attach')}>
                      {t('lead.action.attach')}
                    </Button>
                    <Button variant="secondary" onClick={() => setMode('close')}>
                      {t('lead.action.close')}
                    </Button>
                  </div>
                </>
              ) : null}

              {mode === 'attach' ? (
                <>
                  <SearchField id="pt-lead-attach" label={t('lead.attach.label')} value={search} onChange={setSearch} />
                  {clients.phase === 'ready' && search.trim() !== '' && matches.length === 0 ? (
                    <Statement>{t('lead.attach.none')}</Statement>
                  ) : null}
                  {matches.slice(0, 8).map((row) => (
                    <div className="pt-actions" key={row.clientId}>
                      <Button variant="secondary" onClick={() => putOnFile(row.clientId)} busy={toClient.busy}>
                        {`${row.displayName} · ${row.clientRef}`}
                      </Button>
                    </div>
                  ))}
                  <div className="pt-actions">
                    <Button variant="secondary" onClick={() => setMode('idle')}>
                      {t('lead.back')}
                    </Button>
                  </div>
                </>
              ) : null}

              {mode === 'close' ? (
                <>
                  <TextInput id="pt-lead-close-reason" label={t('lead.close.reason.label')} value={reason} onChange={setReason} multiline autoComplete="off" />
                  <div className="pt-actions">
                    <Button onClick={close} busy={closing.busy}>
                      {t('lead.close.submit')}
                    </Button>
                    <Button variant="secondary" onClick={() => setMode('idle')}>
                      {t('lead.back')}
                    </Button>
                  </div>
                </>
              ) : null}
            </section>
          ) : null}
        </>
      )}
    </AppShell>
  );
}
